import { useEffect, useRef, forwardRef, useMemo, useState } from 'react'
import { Row, Col, Tabs, Table, Checkbox } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { SaveFilled } from '@ant-design/icons'
import { isProdSite, renderTranslation } from 'Helpers/GenericHelpers'
import { TypeOfChannel, iconDictionary } from 'Slices/Omnichannel/DiscountSlice'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { savePubblicationChanges } from 'Services/Kiosk/PubblicationService'
import { ArticlePubbType } from 'Constants/Configuration'
import { WorkSpace } from 'Constants/WorkSpace'

function Pubblications({ style, className, onMouseDown, onMouseUp, onTouchEnd, children, ...props }, ref) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const sites = useAppSelector(state => state.present.configuration.sites)
  const allMarketplace = useAppSelector(state => state.present.configuration.allMarketplace)
  const enabledMarketplaces = useAppSelector(state => state.present.articleDetail.articleSelected.enabledMarketplaces)
  const pubblicationSite = useAppSelector(state => state.present.articleDetail.articleSelected.pubblicationsite)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const workspaceImagesVisible = useAppSelector(state => state.present.configuration.workspaceImagesVisible)
  const publicWorkspace = useAppSelector(state => state.present.configuration.publicWorkspace)
  const isSingleTab = useAppSelector(state => state.present.configuration.isPricesSingleTab)
  const onlySiteWorkspaces = [WorkSpace.WEB, WorkSpace.PIM]
  const defaultTypeOfChannel = onlySiteWorkspaces.includes(publicWorkspace)
    ? TypeOfChannel.Site
    : TypeOfChannel.Marketplace

  const [activeSaveButton, setActiveSaveButton] = useState(false)
  const [dataTable, setDataTable] = useState([])
  const [channelType, setChannelType] = useState(defaultTypeOfChannel)
  const [changePub, setChangedPub] = useState([])
  const tableRef = useRef<any>(undefined)

  useEffect(() => {
    setDataTable(
      allMarketplace
        .filter(
          f =>
            f.enabled === true &&
            (channelType === TypeOfChannel.Marketplace
              ? !f.column.toLowerCase().startsWith('sito')
              : f.column.toLowerCase().startsWith('sito')),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item, index) => {
          return {
            key: item.column,
            value: item.name,
            type:
              channelType === TypeOfChannel.Marketplace
                ? enabledMarketplaces.some(f => f === item.name)
                  ? ArticlePubbType.active
                  : ArticlePubbType.notactive
                : pubblicationSite.some(f => f.key === item.name)
                ? pubblicationSite.filter(f => f.key === item.name).map(e => e.type)[0]
                : ArticlePubbType.notactive,
          }
        }),
    )
  }, [allMarketplace, channelType, enabledMarketplaces, pubblicationSite])

  useEffect(() => {
    setChangedPub([])
    setActiveSaveButton(false)
  }, [articleSelected])

  const saveChanges = e => {
    if (changePub.length === 0) return
    dispatch(savePubblicationChanges(changePub)).finally(() => {
      setChangedPub([])
      setActiveSaveButton(false)
    })
  }

  const handleContent = useMemo(() => {
    const result = Object.values(TypeOfChannel)
      .filter(v => !isNaN(Number(v)))
      .filter(x => (!sites ? x !== TypeOfChannel.Site : true))
      .filter(x => x === TypeOfChannel.Site || x === TypeOfChannel.Marketplace)
      .map(value => {
        const Icon = iconDictionary[value]
        const id = String(value)
        const showMarketplace = !(onlySiteWorkspaces.includes(publicWorkspace) && value === TypeOfChannel.Marketplace)
        return showMarketplace
          ? {
              label: (
                <>
                  <div className='icon-container-tabs'>
                    <Icon className={'prices-tab-icon'} />
                  </div>
                  <div className='discount-type-tab-label'>
                    {renderTranslation(language, TranslationKeys['channelType' + TypeOfChannel[id]])}
                  </div>
                </>
              ),
              key: id,
            }
          : null
      })
    return result
  }, [language, sites])

  const OnChange = (record, value) => {
    setDataTable(prevState => {
      const newData = [...prevState]
      const index = newData.findIndex(item => item.key === record.key)
      if (index > -1 && newData[index].type !== value) {
        newData[index].type = value
        setChangedPub(prev => {
          const existingIndex = prev.findIndex(item => item.key === record.key)
          if (existingIndex !== -1) prev.splice(index, 1)
          return [...prev, newData[index]]
        })
        setActiveSaveButton(true)
      }
      return newData
    })
  }

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: renderTranslation(language, TranslationKeys.columnHeaderAll),
        dataIndex: 'value',
        key: 'value',
      },
      {
        title: renderTranslation(language, TranslationKeys.configurationDropdownPopYes),
        dataIndex: 'yes',
        key: 'yes',
        render: (_, record) => (
          <Checkbox
            checked={record.type === ArticlePubbType.active}
            onChange={e => OnChange(record, ArticlePubbType.active)}
            disabled={isProdSite()}
          />
        ),
      },
      {
        title: renderTranslation(language, TranslationKeys.configurationDropdownPopNo),
        dataIndex: 'no',
        key: 'no',
        render: (_, record) => (
          <Checkbox
            checked={record.type === ArticlePubbType.notactive}
            onChange={e => OnChange(record, ArticlePubbType.notactive)}
            disabled={isProdSite()}
          />
        ),
      },
    ]

    if (channelType === TypeOfChannel.Site) {
      baseColumns.push(
        {
          title: 'Pronto',
          dataIndex: 'pronto',
          key: 'pronto',
          render: (_, record) => (
            <Checkbox
              checked={record.type === ArticlePubbType.pronto}
              onChange={e => OnChange(record, ArticlePubbType.pronto)}
              disabled={isProdSite()}
            />
          ),
        },
        {
          title: renderTranslation(language, TranslationKeys.configurationDropdownPopNo) + 'Ex',
          dataIndex: 'noex',
          key: 'noex',
          render: (_, record) => (
            <Checkbox
              checked={record.type === ArticlePubbType.noex}
              onChange={e => OnChange(record, ArticlePubbType.noex)}
              disabled={isProdSite()}
            />
          ),
        },
      )
    }

    return baseColumns
  }, [language, channelType, dataTable])

  return workspaceImagesVisible ? (
    <Row>
      <Col className='pt-2 pl-2' span={24}>
        <Row>
          <Col>
            <SaveFilled
              className={'generic-icon save-icon' + (activeSaveButton ? ' active' : '')}
              onClick={saveChanges}
            />
          </Col>
        </Row>
        <Row className='w-100 pl-6 ml-6'>
          <Col span={5} className='mt-3'>
            {isProdSite() || isSingleTab ? null : (
              <Tabs
                defaultActiveKey={channelType.toString()}
                animated
                onChange={e => {
                  setChannelType(parseInt(e))
                }}
                items={handleContent}
              />
            )}
          </Col>
        </Row>
        <Row className='pl-6 ml-6 pt-4 w-100' ref={tableRef}>
          <Col span={5}>
            <Table
              className={'discount-setdesc-group-table multi-table same-table-scroll'}
              columns={columns}
              dataSource={dataTable}
              pagination={false}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  ) : null
}

export default forwardRef(Pubblications)
